import { lazy, Suspense, useState } from 'react'
import { useSideNavExpandedLayout } from '@/contexts/side-nav-expanded-context'
import type { RouteObject } from 'react-router'
import { Navigate, Outlet } from 'react-router-dom'
import { IssuerGuard } from '@/guards/issuer-guard'
import { AuthGuard } from '@/guards/auth-guard'
import { Issuer } from '@/types/auth'
import { Layout as DashboardLayout } from '@/layouts/dashboard'
import type { DashboardOutletContextValue } from '@/layouts/dashboard/dashboard-outlet-context'
import { Role } from '@/api'
import { wrapWithErrorBoundary } from './wrapWithErrorBoundary'

const BackofficePage = lazy(() => import('@/pages/backoffice'))
const UserEditPage = lazy(() => import('@/pages/backoffice/users/edit'))
const OrganizationEditPage = lazy(
  () => import('@/pages/backoffice/organizations/edit')
)
const GroupEditPage = lazy(
  () => import('@/pages/backoffice/organizations/groups/edit')
)

const WrappedBackofficePage = wrapWithErrorBoundary(BackofficePage)
const WrappedUserEditPage = wrapWithErrorBoundary(UserEditPage)
const WrappedOrganizationEditPage = wrapWithErrorBoundary(OrganizationEditPage)
const WrappedGroupEditPage = wrapWithErrorBoundary(GroupEditPage)

const BackofficeLayoutWrapper = (props: any) => {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const { sideNavExpanded, setSideNavExpanded } = useSideNavExpandedLayout()

  const outletContext: DashboardOutletContextValue = {
    setIsFullscreen,
    sideNavExpanded,
    setSideNavExpanded,
  }

  return (
    <IssuerGuard issuer={Issuer.JWT}>
      <DashboardLayout
        isFullscreen={isFullscreen}
        sideNavExpanded={sideNavExpanded}
        setSideNavExpanded={setSideNavExpanded}
      >
        <AuthGuard role={[Role.Superadmin]}>
          <Suspense>
            <Outlet context={outletContext} />
          </Suspense>
        </AuthGuard>
      </DashboardLayout>
    </IssuerGuard>
  )
}

export const backofficeRoutes: RouteObject[] = [
  {
    path: 'backoffice',
    element: <BackofficeLayoutWrapper />,
    children: [
      {
        index: true,
        element: <WrappedBackofficePage />,
        handle: {
          documentTitle: { namespace: 'nav', key: 'backoffice' },
        },
      },
      {
        path: 'users',
        children: [
          {
            index: true,
            element: <Navigate to="/backoffice?tab=users" replace />,
          },
          {
            path: ':userId/edit',
            element: <WrappedUserEditPage />,
            handle: {
              documentTitle: { namespace: 'nav', key: 'users' },
            },
          },
        ],
      },
      {
        path: 'organizations',
        children: [
          {
            index: true,
            element: <Navigate to="/backoffice?tab=organizations" replace />,
          },
          {
            path: ':organizationId/edit',
            element: <WrappedOrganizationEditPage />,
            handle: {
              documentTitle: { namespace: 'nav', key: 'organizations' },
            },
          },
          {
            path: ':organizationId/groups/:groupId/edit',
            element: <WrappedGroupEditPage />,
            handle: {
              documentTitle: { namespace: 'nav', key: 'organizations' },
            },
          },
        ],
      },
      {
        path: 'connectors',
        children: [
          {
            index: true,
            element: <Navigate to="/backoffice?tab=connectors" replace />,
          },
        ],
      },
    ],
  },
]
